import {
  APICurrentUser,
  APIOrganizationUser,
  PrivilegeLabel,
} from '@ardoq/api-types';
import styled from 'styled-components';
import {
  ImpersonationBanner,
  toImpersonationBannerProps,
} from '@ardoq/impersonation';
import { currentUserHasPrivilege } from '@ardoq/privileges';
import { text1, header1 } from '@ardoq/typography';
import { Link } from '@ardoq/link';
import { ReactNode } from 'react';
import { colors, radius, spacing } from '@ardoq/design-tokens';
import { Box, Stack } from '@ardoq/layout';
import { toArdoqError } from '@ardoq/common-helpers';
import { ErrorState } from '@ardoq/status-ui';
import { withBasePath } from 'basePath';
import {
  DEFAULT_ARDOQ_I18NLOCALE,
  t,
  Trans,
  type I18nLocale,
} from '@ardoq/i18n';
import { VisuallyHidden } from '@react-aria/visually-hidden';
import { LoadingScreenLogo } from '@ardoq/loader';

type AccessErrorProps = {
  user: APICurrentUser;
  i18nLocale?: I18nLocale;
};

const FullPageGridLayout = styled.div`
  height: 100vh;
  display: grid;
  grid-template-rows: auto 1fr;
`;

const Wrapper = styled.div`
  height: 100%;
  width: 100%;
  color: ${colors.white950};
  background-color: ${colors.surfaceBrand};
  display: grid;
  z-index: 1050;
  grid-template-rows: min-content 1fr;
  align-items: start;
`;

const MessageBox = styled.div`
  padding: ${spacing.s32};
  background-color: ${colors.white950};
  width: 533px;
  border-radius: ${radius.r16};
  place-self: center;
  margin-top: -80px;
`;

const centeredLoadingScreenLogoStyle = {
  position: 'relative',
  placeSelf: 'center',
} as const;

const Header = styled.h1`
  ${header1};
  margin: 0;
  color: ${colors.black950};
  margin-bottom: ${spacing.s16};
`;

const Body = styled.div`
  ${text1}
  color: ${colors.grey500};
  display: grid;
  gap: ${spacing.s16};
  & > p {
    margin: 0;
  }
`;

type AccessErrorWrapperProps = {
  header: string;
  children: ReactNode;
  user?: APICurrentUser;
};

const AccessErrorWrapper = ({
  header,
  children,
  user,
}: AccessErrorWrapperProps) => {
  return (
    <Wrapper>
      <LoadingScreenLogo style={centeredLoadingScreenLogoStyle} />
      <MessageBox>
        <Header>{header}</Header>
        <Body>
          {children}
          {user && (
            <p>
              <Trans
                i18nKey="auth:accessError.signedInAs"
                values={{ email: user.email }}
                defaults="You are currently signed in as <bold>{{email}}</bold>. <logoutLink>Log out</logoutLink> to sign in as a different user."
                components={{
                  bold: <b />,
                  logoutLink: <Link hideIcon href="/logout" />,
                }}
              />
            </p>
          )}
        </Body>
      </MessageBox>
    </Wrapper>
  );
};

export const AccessError = ({
  user,
  i18nLocale = DEFAULT_ARDOQ_I18NLOCALE,
}: AccessErrorProps) => {
  const isDiscoverUser = currentUserHasPrivilege(
    user,
    PrivilegeLabel.ACCESS_DISCOVER
  );
  return (
    <AccessErrorWrapper
      header={t(i18nLocale, 'auth:accessError.noAccess.header', 'No access')}
      user={user}
    >
      <p>
        {t(
          i18nLocale,
          'auth:accessError.noAccess.intro',
          'Sorry, it looks like you don’t have access to core Ardoq. Worry not, though!'
        )}
      </p>
      <p>
        {isDiscoverUser && (
          <>
            {t(
              i18nLocale,
              'auth:accessError.noAccess.discoverPrompt',
              'Looking for real-time contextual insights?'
            )}
            <br />
            <Link hideIcon href={withBasePath('/discover')}>
              {t(
                i18nLocale,
                'auth:accessError.noAccess.accessDiscover',
                'Access Ardoq Discover'
              )}
            </Link>
            <br />
          </>
        )}
        {t(
          i18nLocale,
          'auth:accessError.noAccess.contributePrompt',
          'Would you like to contribute your knowledge?'
        )}
        <br />
        <Link hideIcon href={withBasePath('/surveys')}>
          {t(
            i18nLocale,
            'auth:accessError.noAccess.accessSurveys',
            'Access Ardoq Surveys'
          )}
        </Link>
      </p>
      <p>
        {t(
          i18nLocale,
          'auth:accessError.noAccess.contactAdministrator',
          'If you still need access to core Ardoq, please contact your administrator.'
        )}
      </p>
    </AccessErrorWrapper>
  );
};

export const ImpersonationAccessError = ({
  user,
  i18nLocale = DEFAULT_ARDOQ_I18NLOCALE,
}: AccessErrorProps) => (
  <FullPageGridLayout>
    <ImpersonationBanner {...toImpersonationBannerProps(user, i18nLocale)} />
    <AccessError user={user} i18nLocale={i18nLocale} />
  </FullPageGridLayout>
);

export const AccessErrorPresentation = ({
  user,
  i18nLocale = DEFAULT_ARDOQ_I18NLOCALE,
}: AccessErrorProps) => {
  return (
    <AccessErrorWrapper
      header={t(
        i18nLocale,
        'auth:accessError.presentation.header',
        'No access to presentation'
      )}
      user={user}
    >
      <p>
        {t(
          i18nLocale,
          'auth:accessError.presentation.body',
          "Sorry, it looks like you don't have access to this presentation. If you need access to the presentation, please contact your administrator."
        )}
      </p>
    </AccessErrorWrapper>
  );
};

type NoOrgAccessErrorPresentationProps = {
  i18nLocale?: I18nLocale;
};

export const NoOrgAccessErrorPresentation = ({
  i18nLocale = DEFAULT_ARDOQ_I18NLOCALE,
}: NoOrgAccessErrorPresentationProps) => {
  return (
    <AccessErrorWrapper
      header={t(
        i18nLocale,
        'auth:accessError.noOrgAccess.header',
        'No access to organization'
      )}
    >
      <p>
        {t(
          i18nLocale,
          'auth:accessError.noOrgAccess.body',
          "You don't have access to this organization. If you think you should, please contact the organization administrator."
        )}
      </p>
      <p>
        <Trans
          i18nKey="auth:accessError.noOrgAccess.contactSupport"
          defaults="Alternatively, you can also contact <supportLink>support@ardoq.com</supportLink>."
          components={{
            supportLink: <Link hideIcon href="mailto:support@ardoq.com" />,
          }}
        />
      </p>
      <p>
        <Link hideIcon href="https://app.ardoq.com">
          {t(
            i18nLocale,
            'auth:accessError.noOrgAccess.takeMeBack',
            'Take me back'
          )}
        </Link>
        {t(i18nLocale, 'auth:accessError.noOrgAccess.or', ' or ')}
        <Link hideIcon href="/logout">
          {t(i18nLocale, 'auth:accessError.noOrgAccess.logOut', 'log out')}
        </Link>
      </p>
    </AccessErrorWrapper>
  );
};

type PresentationNotFoundProps = {
  i18nLocale?: I18nLocale;
};

export const PresentationNotFound = ({
  i18nLocale = DEFAULT_ARDOQ_I18NLOCALE,
}: PresentationNotFoundProps) => {
  return (
    <AccessErrorWrapper
      header={t(
        i18nLocale,
        'auth:accessError.presentationNotFound.header',
        'Presentation not found'
      )}
    >
      <p>
        {t(
          i18nLocale,
          'auth:accessError.presentationNotFound.body',
          'Sorry, this presentation could not be found.'
        )}
      </p>
    </AccessErrorWrapper>
  );
};

const ErrorStateBox = styled(Box)`
  color: ${colors.textDefault};
  place-self: center;
  margin-top: -80px;
`;

type PresentationLoadErrorProps = {
  i18nLocale?: I18nLocale;
};

export const PresentationLoadError = ({
  i18nLocale = DEFAULT_ARDOQ_I18NLOCALE,
}: PresentationLoadErrorProps) => {
  const title = t(
    i18nLocale,
    'auth:accessError.presentationLoadError.header',
    'Presentation unavailable'
  );
  const message = t(
    i18nLocale,
    'auth:accessError.presentationLoadError.body',
    "This presentation isn't available. Contact the person who shared it with you."
  );
  return (
    <FullPageGridLayout>
      <LoadingScreenLogo style={centeredLoadingScreenLogoStyle} />
      <ErrorStateBox borderRadius="r8" padding="xlarge">
        <VisuallyHidden elementType="h1">{title}</VisuallyHidden>
        <ErrorState
          layout="vertical"
          role="alert"
          title={title}
          error={toArdoqError({
            error: new Error('Presentation failed to load'),
            userMessage: message,
          })}
        />
      </ErrorStateBox>
    </FullPageGridLayout>
  );
};

type DiscoverAccessErrorProps = AccessErrorProps & {
  orgAdmins: APIOrganizationUser[];
};

const toAdminsLeadIn = (count: number, i18nLocale: I18nLocale): string =>
  count === 1
    ? t(
        i18nLocale,
        'auth:accessError.discover.adminsLeadIn_one',
        { count },
        'Your admin:'
      )
    : t(
        i18nLocale,
        'auth:accessError.discover.adminsLeadIn_other',
        { count },
        'Your admins:'
      );

const ADMINS_LEAD_IN_ID = 'discover-access-error-admins-lead-in';

const AdminList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  max-height: 160px;
  overflow-y: auto;
`;

export const DiscoverAccessError = ({
  user,
  orgAdmins,
  i18nLocale = DEFAULT_ARDOQ_I18NLOCALE,
}: DiscoverAccessErrorProps) => {
  return (
    <AccessErrorWrapper
      header={t(
        i18nLocale,
        'auth:accessError.discover.header',
        'No access to Discover'
      )}
      user={user}
    >
      <p>
        {t(
          i18nLocale,
          'auth:accessError.discover.body',
          'You don’t have access to Discover. If you need access, contact your admin.'
        )}
      </p>
      {orgAdmins.length > 0 && (
        <Stack gap="xsmall">
          <span id={ADMINS_LEAD_IN_ID}>
            {toAdminsLeadIn(orgAdmins.length, i18nLocale)}
          </span>
          <AdminList role="list" aria-labelledby={ADMINS_LEAD_IN_ID}>
            {orgAdmins.map(({ _id, name, email }) => (
              <li key={_id}>
                {name} (
                <Link hideIcon href={`mailto:${email}`}>
                  {email}
                </Link>
                )
              </li>
            ))}
          </AdminList>
        </Stack>
      )}
    </AccessErrorWrapper>
  );
};
